import { defineStore } from "pinia";
import schedulesApi, { ActiveScheduleResponse } from "../Api/schedules";
import eventSettingsApi from "../Api/eventSettingsApi";
import { Schedule } from "../Common/Schedule/types";
import { translate } from "../Common/translations";

export type SharingTypeOption = {
    key: string;
    value: number;
    text: string;
};

type SaveScheduleData = {
    scheduleId: number | null;
    scheduleName: string;
    scheduleSharing: number;
    scheduleStartTime: string;
};

export const useSchedulesListingStore = defineStore("schedulesListing", {
    state: () => ({
        eventId: 0,

        schedules: [] as Schedule[],
        activeSchedule: {} as Schedule | Record<string, never>,
        sharingTypes: [] as SharingTypeOption[],
        // null until loaded, so the toolbar shows no status when the read fails.
        sharePublicFightsQueue: null as boolean | null,
    }),

    actions: {
        fetchSchedules(): Promise<void> {
            return schedulesApi.getSchedules(this.eventId).then(({ all_schedules, active_schedule }: ActiveScheduleResponse) => {
                this.schedules = all_schedules;
                this.activeSchedule = active_schedule;
            });
        },
        fetchSupportedSharingTypes(): Promise<void> {
            return schedulesApi.getSupportedSharingTypes(this.eventId).then(sharingTypes => {
                this.sharingTypes = sharingTypes.map(el => ({
                    key: el.key,
                    value: el.value,
                    text: translate(el.key),
                }));
            });
        },
        fetchFightsQueueSharing(): Promise<void> {
            return eventSettingsApi.getSingleSetting(this.eventId, "sharePublicFightsQueue").then(setting => {
                this.sharePublicFightsQueue = setting.value;
            });
        },
        updateFightsQueueSharing(enabled: boolean): Promise<void> {
            return eventSettingsApi.updateSetting(this.eventId, "sharePublicFightsQueue", enabled).then(() => {
                this.sharePublicFightsQueue = enabled;
            });
        },
        refreshListing(): Promise<void> {
            return Promise.all([this.fetchSupportedSharingTypes().then(() => this.fetchSchedules()), this.fetchFightsQueueSharing()]).then(
                () => undefined,
            );
        },
        deleteSchedule(scheduleId: number): Promise<void> {
            return schedulesApi.deleteSchedule(this.eventId, scheduleId).then(() => this.fetchSchedules());
        },
        activateSchedule(scheduleId: number): Promise<void> {
            return schedulesApi.activateSchedule(this.eventId, scheduleId).then(() => this.fetchSchedules());
        },
        erasePlannedVersion(scheduleId: number): Promise<void> {
            return schedulesApi.erasePlannedVersion(this.eventId, scheduleId).then(() => this.fetchSchedules());
        },
        saveSchedule(newData: SaveScheduleData): Promise<void> {
            return schedulesApi
                .updateSchedule(this.eventId, {
                    scheduleId: newData.scheduleId,
                    eventId: this.eventId,
                    scheduleName: newData.scheduleName,
                    scheduleSharing: newData.scheduleSharing,
                    startTime: newData.scheduleStartTime,
                })
                .then(() => this.fetchSchedules());
        },
        updateScheduleSharing(scheduleId: number, sharing: number, setAsPlanned: boolean): Promise<void> {
            return schedulesApi.updateScheduleSharing(this.eventId, scheduleId, sharing, setAsPlanned).then(() => this.fetchSchedules());
        },
    },
});
